import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { visibleTabs } from '../lib/tabs'

export interface Tab {
  key: string
  label: string
  /** Affiche entre parentheses apres le libelle. */
  count?: number | string
  content: ReactNode
}

const tabClass = 'shrink-0 whitespace-nowrap border-b-2 pb-2 text-xs font-semibold uppercase tracking-wide'

/**
 * Onglets : un seul panneau a l'ecran, les fleches passent de l'un a l'autre.
 * Ceux qui ne tiennent pas sur la ligne sont ranges derriere un bouton « ... » :
 * ni barre de defilement, ni seconde ligne.
 */
export default function Tabs({
  label,
  tabs,
  selected: imposed,
  onSelect,
}: {
  label: string
  tabs: Tab[]
  /** Onglet ouvert, quand c'est l'appelant qui le decide (avec `onSelect`). */
  selected?: string
  onSelect?: (key: string) => void
}) {
  const baseId = useId()
  const [own, setOwn] = useState(tabs[0]?.key)
  const selected = imposed ?? own
  const setSelected = (key: string) => {
    setOwn(key)
    onSelect?.(key)
  }
  // Un onglet peut disparaitre (donnees relues, type d'evenement change) : on retombe sur le premier.
  const current = tabs.find((tab) => tab.key === selected) ?? tabs[0]
  const currentIndex = current === undefined ? 0 : tabs.indexOf(current)

  const bar = useRef<HTMLDivElement>(null)
  const ruler = useRef<HTMLDivElement>(null)
  const more = useRef<HTMLDivElement>(null)
  // `null` : tous. C'est aussi le cas sans mise en page mesurable.
  const [shown, setShown] = useState<number[] | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const signature = tabs.map((tab) => `${tab.key}:${tab.label}:${tab.count ?? ''}`).join('|')

  useLayoutEffect(() => {
    const measure = () => {
      const available = bar.current?.clientWidth ?? 0
      const marks = [...(ruler.current?.children ?? [])] as HTMLElement[]
      if (available === 0 || marks.length < 2) {
        setShown(null)
        return
      }

      const widths = marks.slice(0, -1).map((mark) => mark.offsetWidth)
      const next = visibleTabs(widths, currentIndex, available, marks[marks.length - 1]!.offsetWidth)
      setShown((previous) =>
        next.length === widths.length ? null : previous !== null && previous.join() === next.join() ? previous : next,
      )
    }

    measure()
    if (bar.current === null || typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(measure)
    observer.observe(bar.current)
    return () => observer.disconnect()
  }, [signature, currentIndex])

  useEffect(() => {
    if (!menuOpen) {
      return
    }

    const close = (event: MouseEvent) => {
      if (!more.current?.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [menuOpen])

  if (current === undefined) {
    return null
  }

  const visible = shown === null ? tabs : shown.map((index) => tabs[index]).filter((tab) => tab !== undefined)
  const hidden = tabs.filter((tab) => !visible.includes(tab))

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = visible.indexOf(current)
    const target = {
      ArrowRight: (index + 1) % visible.length,
      ArrowLeft: (index - 1 + visible.length) % visible.length,
      Home: 0,
      End: visible.length - 1,
    }[event.key]

    if (target === undefined) {
      return
    }

    event.preventDefault()
    setSelected(visible[target]!.key)
    event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[target]?.focus()
  }

  const caption = (tab: Tab) => (
    <>
      {tab.label}
      {tab.count !== undefined && <span className="ml-1 font-normal normal-case">({tab.count})</span>}
    </>
  )

  return (
    <div>
      <div ref={bar} className="relative flex border-b border-neutral-200 dark:border-neutral-800">
        {/* Regle invisible : la largeur de chaque onglet et du bouton « ... », pour savoir lesquels tiennent. */}
        <div ref={ruler} aria-hidden="true" className="invisible absolute top-0 left-0 flex h-0 overflow-hidden">
          {tabs.map((tab) => (
            <span key={tab.key} className={tabClass}>
              {caption(tab)}
            </span>
          ))}
          <span className={`${tabClass} px-1`}>•••</span>
        </div>

        <div role="tablist" aria-label={label} className="flex min-w-0 flex-1 gap-x-5">
          {visible.map((tab) => {
            const active = tab.key === current.key
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                id={`${baseId}-tab-${tab.key}`}
                aria-selected={active}
                aria-controls={`${baseId}-panel`}
                tabIndex={active ? 0 : -1}
                onClick={() => setSelected(tab.key)}
                onKeyDown={onKeyDown}
                className={`-mb-px transition ${tabClass} ${
                  active
                    ? 'border-neutral-900 text-neutral-900 dark:border-neutral-100 dark:text-neutral-50'
                    : 'border-transparent text-neutral-400 hover:text-neutral-700 dark:text-neutral-500 dark:hover:text-neutral-300'
                }`}
              >
                {caption(tab)}
              </button>
            )
          })}
        </div>

        {hidden.length > 0 && (
          <div
            ref={more}
            className="relative shrink-0"
            onKeyDown={(event) => {
              // Echap referme ce menu, pas la fenetre qui le contient.
              if (event.key === 'Escape' && menuOpen) {
                event.stopPropagation()
                setMenuOpen(false)
              }
            }}
          >
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={`Autres rubriques (${hidden.length})`}
              onClick={() => setMenuOpen((previous) => !previous)}
              className={`-mb-px border-transparent px-1 text-neutral-500 transition hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 ${tabClass}`}
            >
              <span aria-hidden="true">•••</span>
            </button>
            {menuOpen && (
              <div
                role="menu"
                aria-label="Autres rubriques"
                className="absolute right-0 z-10 mt-1 min-w-48 rounded-lg border border-neutral-200 bg-white p-1 shadow-lg dark:border-neutral-800 dark:bg-neutral-900"
              >
                {hidden.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSelected(tab.key)
                      setMenuOpen(false)
                    }}
                    className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800"
                  >
                    {tab.label}
                    {tab.count !== undefined && <span className="text-xs text-neutral-500">{tab.count}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div role="tabpanel" id={`${baseId}-panel`} aria-labelledby={`${baseId}-tab-${current.key}`} className="pt-3">
        {current.content}
      </div>
    </div>
  )
}
